import { useEffect, useState } from 'react'
import {
  getUserData,
  getUserDataOrganization,
} from '../../services/ClienteDados/getClientData'
import { DadosLoginChat } from './movidesk'

// Identificação a enviar ao Movidesk; `null` = o chat não deve aparecer (tenant com o chat
// desligado, sem sessão, ou ainda não resolvido).
type DadosChat = DadosLoginChat | null

// O `/me` não muda durante a sessão, e há outros dois consumidores dos mesmos endpoints
// (`useTopUserData`, `useModulosTenant`). O cache evita repetir o par de chamadas nas transições
// que remontam o LayoutPage — seleção de organização, login e logout. A chave inclui o token e o
// tenant: outro login (ou outra organização) invalida sozinho, sem depender de o logout limpar
// nada. ⚠️ Só resultado bem-sucedido entra: cachear a falha faria um 500 transitório desligar o
// chat até a próxima recarga da página.
let cache: { chave: string; promessa: Promise<DadosChat> } | null = null

const buscarDados = async (tenantId: string): Promise<DadosChat> => {
  const [usuario, vinculo] = await Promise.all([
    getUserData(),
    getUserDataOrganization(tenantId),
  ])
  if ('message' in usuario || 'message' in vinculo) {
    cache = null
    return null
  }
  // Só desliga quando o backend diz explicitamente que está desligado: o padrão do produto é o
  // suporte disponível, e a API antiga (sem o campo) não deve esconder o chat.
  if (vinculo.chatSuporteAtivo === false) {
    return null
  }
  // O widget do Movidesk não tem campo livre para contexto: os únicos dados que chegam ao agente
  // são estes. Por isso a organização vai embutida no nome — é o que faz o suporte saber de qual
  // empresa é a pessoa antes da primeira pergunta.
  const empresa = vinculo.nomeOrganizacao
    ? `${vinculo.nomeOrganizacao} (${tenantId})`
    : tenantId
  return {
    name: `${usuario.nome} — ${empresa}`,
    email: usuario.email,
    // Usado pelo Movidesk apenas quando o contato ainda não existe, para já nascer associado
    // à organização certa.
    organizationCodeReference: tenantId,
    // Não manter a sessão do chat presa ao navegador: a máquina pode ser compartilhada e o
    // logout do Cartivia não tem como encerrar a sessão do widget.
    stayConnected: false,
    // Assunto do ticket fica com o nome do cliente (o agente pode trocar depois).
    emptySubject: false,
    // Nunca abrir a conversa sozinha: o chat entra como canal disponível, não como interrupção.
    startChat: false,
  }
}

/**
 * Resolve com quais dados o usuário é identificado no chat, ou `null` se o chat não deve aparecer.
 * Sem sessão, sem organização selecionada ou fora da visualização do Cartivia, nem chega a
 * consultar a API — não há a quem identificar, e as telas administrativas não exibem o chat.
 */
export const useChatSuporte = (naVisualizacaoCartivia: boolean): DadosChat => {
  const [dados, setDados] = useState<DadosChat>(null)

  useEffect(() => {
    let montado = true
    const token = localStorage.getItem('token')
    const xtenant = localStorage.getItem('X-TENANT-ID')
    if (!naVisualizacaoCartivia || token === null || xtenant === null) {
      return undefined
    }
    // X-TENANT-ID é guardado com aspas no localStorage (JSON.stringify); slice remove as aspas.
    const tenantId = xtenant.slice(1, -1)
    const chave = `${token}|${tenantId}`
    if (cache === null || cache.chave !== chave) {
      cache = { chave, promessa: buscarDados(tenantId) }
    }
    cache.promessa.then(resultado => {
      if (montado) setDados(resultado)
    })
    return () => {
      montado = false
    }
  }, [naVisualizacaoCartivia])

  return dados
}
